import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button, Box, Modal, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";
import styled from "@emotion/styled";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../../utils/cropImage";
import { resizeImage } from "../../../utils/resizeImage";
import { uploadProfileImageToFirebase } from "../../../services/firebaseStorage";
import { useTheme } from "styled-components";

const VisuallyHiddenInput = styled("input")({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
});

export default function ImageUploader({
  uploadTrigger,
  onImageUpload,
  userId,
}) {
  const theme = useTheme();
  const inputRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // whenever parent increments this trigger, open file picker
  useEffect(() => {
    if (uploadTrigger > 0) {
      inputRef.current?.click();
    }
  }, [uploadTrigger]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      // We can keep your resize helper as-is. It helps performance on mobile.
      // If you want a higher-res avatar, bump the target size here.
      resizeImage(file, 550, 710, (resizedBlob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
          setOpen(true);
          setZoom(1);
          setCrop({ x: 0, y: 0 });
        };
        reader.readAsDataURL(resizedBlob);
      });
    }
    // allow re-selecting the same file later
    if (inputRef.current) inputRef.current.value = "";
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCrop = async () => {
    if (!preview || !croppedAreaPixels) return;
    setIsUploading(true);
    try {
      // Create a circular PNG with transparency
      const croppedPngBlob = await getCroppedImg(preview, croppedAreaPixels, {
        roundMask: true,
        mimeType: "image/png",
        quality: 0.92, // ignored for PNG but harmless if you later switch to JPEG
      });

      // Upload the PNG blob; your upload service should accept Blob/File
      const url = await uploadProfileImageToFirebase(croppedPngBlob, userId);

      // Let parent know where the file lives
      await onImageUpload(url);

      setOpen(false);
      setPreview(null);
      setCroppedAreaPixels(null);
    } catch (error) {
      console.error("Error during crop/upload:", error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <VisuallyHiddenInput
        type="file"
        accept="image/*"
        ref={inputRef}
        onChange={handleFileChange}
      />

      <Modal open={open} onClose={() => (isUploading ? null : setOpen(false))}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
            backgroundColor: alpha(theme.palette.common.black, 0.75),
          }}
        >
          {preview && (
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"          // ← visually round
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              style={{
                containerStyle: {
                  width: "100%",
                  height: "100%",
                },
              }}
            />
          )}

          <Box
            sx={{
              position: "fixed",
              bottom: 72,
              display: "flex",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={() => setOpen(false)}
              sx={{
                backgroundColor: theme.colors.darkGray,
                fontWeight: "bold",
                color: theme.colors.white,
                px: 3,
                "&:hover": {
                  backgroundColor: theme.colors.lightGray,
                  color: theme.colors.darkGray,
                },
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCrop}
              disabled={isUploading}
              sx={{
                backgroundColor: theme.colors.blue,
                fontWeight: "bold",
                color: theme.colors.white,
                px: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  backgroundColor: theme.colors.green,
                },
              }}
            >
              {isUploading ? (
                <CircularProgress size={20} sx={{ color: theme.palette.common.white }} />
              ) : (
                "Crop Circle & Upload"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
